import { useState, useRef, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X, ChevronDown, RefreshCcw, BarChart3, CreditCard, Users } from "lucide-react";
import SlotifyLogo from "@/components/SlotifyLogo";
import { useLanguage } from "@/contexts/LanguageContext";

const extensions = [
  {
    to: "/extensions/self-service",
    icon: RefreshCcw,
    emoji: "🔄",
    titleKey: "extpage.ss_title",
    descKey: "ext.self_service_desc",
  },
  {
    to: "/extensions/statistics",
    icon: BarChart3,
    emoji: "📊",
    titleKey: "extpage.stats_title",
    descKey: "ext.statistics_desc",
  },
  {
    to: "/extensions/pay-at-order",
    icon: CreditCard,
    emoji: "💳",
    titleKey: "extpage.pao_title",
    descKey: "ext.pao_card_desc",
  },
  {
    to: "/extensions/employees",
    icon: Users,
    emoji: "👥",
    titleKey: "extpage.emp_title",
    descKey: "ext.employees_desc",
  },
];

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const megaRef = useRef<HTMLDivElement>(null);
  const megaTimeout = useRef<ReturnType<typeof setTimeout>>();
  const { t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();

  const handleAnchorClick = (hash: string) => {
    if (location.pathname !== "/") {
      navigate("/" + hash);
    } else {
      const el = document.querySelector(hash);
      el?.scrollIntoView({ behavior: "smooth" });
    }
  };

  // Close mega menu on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (megaRef.current && !megaRef.current.contains(e.target as Node)) {
        setMegaOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleMegaEnter = () => {
    clearTimeout(megaTimeout.current);
    setMegaOpen(true);
  };

  const handleMegaLeave = () => {
    megaTimeout.current = setTimeout(() => setMegaOpen(false), 200);
  };

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 border-b border-border/50 bg-card/80 backdrop-blur-xl">
        <div className="container mx-auto flex h-16 items-center justify-between px-4">
          <Link to="/" className="flex items-center gap-2">
            <SlotifyLogo className="h-7" />
          </Link>

          <div className="hidden items-center gap-8 md:flex">
            <a href="/#features" onClick={(e) => { e.preventDefault(); handleAnchorClick("#features"); }} className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground">
              {t("landing.nav_features")}
            </a>

            {/* Extensions mega menu */}
            <div
              ref={megaRef}
              className="relative"
              onMouseEnter={handleMegaEnter}
              onMouseLeave={handleMegaLeave}
            >
              <button
                onClick={() => setMegaOpen((v) => !v)}
                className="flex items-center gap-1 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
              >
                {t("landing.nav_extensions")}
                <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${megaOpen ? "rotate-180" : ""}`} />
              </button>

              {megaOpen && (
                <div className="absolute left-1/2 top-full pt-3 -translate-x-1/2">
                  <div className="w-[420px] rounded-xl border border-border bg-card p-2 shadow-lg animate-in fade-in-0 zoom-in-95 duration-150">
                    {extensions.map((ext) => (
                      <Link
                        key={ext.to}
                        to={ext.to}
                        onClick={() => setMegaOpen(false)}
                        className="group flex items-start gap-3 rounded-lg p-3 transition-colors hover:bg-muted"
                      >
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-lg transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                          {ext.emoji}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-foreground">{t(ext.titleKey)}</p>
                          <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground line-clamp-2">{t(ext.descKey)}</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <a href="/#how-it-works" onClick={(e) => { e.preventDefault(); handleAnchorClick("#how-it-works"); }} className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground">
              {t("landing.nav_how")}
            </a>
            <a href="/#testimonials" onClick={(e) => { e.preventDefault(); handleAnchorClick("#testimonials"); }} className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground">
              {t("landing.nav_testimonials")}
            </a>
            <a href="/#pricing" onClick={(e) => { e.preventDefault(); handleAnchorClick("#pricing"); }} className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground">
              {t("landing.nav_pricing")}
            </a>
          </div>

          <div className="hidden items-center gap-3 md:flex">
            <Button variant="ghost" asChild>
              <Link to="/login">{t("landing.nav_login")}</Link>
            </Button>
            <Button asChild>
              <Link to="/signup">{t("landing.nav_signup")}</Link>
            </Button>
          </div>

          {/* Mobile hamburger */}
          <Button
            variant="ghost"
            size="icon"
            className="relative z-[60] md:hidden"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
        </div>
      </nav>

      {/* Mobile dropdown - outside nav to avoid backdrop-blur stacking context */}
      {mobileOpen && (
        <div className="fixed inset-0 top-16 z-50 bg-card/95 backdrop-blur-xl md:hidden overflow-y-auto">
          <div className="flex flex-col justify-between min-h-full px-6 py-6">
            <div className="flex flex-col gap-1">
              <a href="/#features" onClick={(e) => { e.preventDefault(); setMobileOpen(false); handleAnchorClick("#features"); }} className="rounded-lg px-3 py-3.5 text-base font-medium text-muted-foreground hover:bg-muted hover:text-foreground">
                {t("landing.nav_features")}
              </a>

              {/* Mobile extensions */}
              <div className="px-3 py-2">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground/60 mb-2">
                  {t("landing.nav_extensions")}
                </p>
                <div className="flex flex-col gap-1">
                  {extensions.map((ext) => (
                    <Link
                      key={ext.to}
                      to={ext.to}
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center gap-3 rounded-lg px-2 py-3 text-base font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
                    >
                      <span className="text-lg">{ext.emoji}</span>
                      <span>{t(ext.titleKey)}</span>
                    </Link>
                  ))}
                </div>
              </div>

              <a href="/#how-it-works" onClick={(e) => { e.preventDefault(); setMobileOpen(false); handleAnchorClick("#how-it-works"); }} className="rounded-lg px-3 py-3.5 text-base font-medium text-muted-foreground hover:bg-muted hover:text-foreground">
                {t("landing.nav_how")}
              </a>
              <a href="/#testimonials" onClick={(e) => { e.preventDefault(); setMobileOpen(false); handleAnchorClick("#testimonials"); }} className="rounded-lg px-3 py-3.5 text-base font-medium text-muted-foreground hover:bg-muted hover:text-foreground">
                {t("landing.nav_testimonials")}
              </a>
              <a href="/#pricing" onClick={(e) => { e.preventDefault(); setMobileOpen(false); handleAnchorClick("#pricing"); }} className="rounded-lg px-3 py-3.5 text-base font-medium text-muted-foreground hover:bg-muted hover:text-foreground">
                {t("landing.nav_pricing")}
              </a>
            </div>

            <div className="flex flex-col gap-3 border-t border-border pt-6 mt-6">
              <Button variant="ghost" size="lg" asChild className="justify-center text-base">
                <Link to="/login" onClick={() => setMobileOpen(false)}>{t("landing.nav_login")}</Link>
              </Button>
              <Button size="lg" asChild className="text-base">
                <Link to="/signup" onClick={() => setMobileOpen(false)}>{t("landing.nav_signup")}</Link>
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
